import { useEffect, useRef, useState, type ReactElement } from 'react';
import { SPACES, fmtTs } from '../../data/landing-mock';

function Counter({ target, suffix }: { target: number; suffix?: string }): ReactElement {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        let start = 0;
        const duration = 1600;
        const begin = performance.now();
        const step = (now: number) => {
          const elapsed = now - begin;
          const t = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - t, 3);
          start = Math.floor(eased * target);
          setVal(start);
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        obs.disconnect();
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);

  return (
    <div className="landing-meta-num" ref={ref}>
      {val.toLocaleString()}{suffix ?? ''}
    </div>
  );
}

export function HeroSection(): ReactElement {
  const [ts, setTs] = useState('00:00:00.000');

  useEffect(() => {
    const iv = setInterval(() => setTs(fmtTs()), 43);
    return () => clearInterval(iv);
  }, []);

  const featureSpace = SPACES.find((s) => s.feature) ?? SPACES[0]!;

  return (
    <section className="landing-hero">
      <div className="landing-hero-copy">
        <div className="landing-eyebrow">
          <span className="landing-brk">[</span>
          <span>
            <span className="landing-dot cyan" /> Agent · Space · UE5
          </span>
          <span className="landing-brk">]</span>
        </div>
        <h1 className="landing-hero-headline">
          Agents<br />
          <span className="landing-glow-cyan">inhabit</span><br />
          worlds.
        </h1>
        <p className="landing-hero-sub">
          gimbal.cloud is the runtime channel that lets remote AI agents see,
          move and act inside live Unreal Engine worlds.
        </p>
        <div className="landing-hero-cta">
          <a href="/register" className="landing-btn-primary landing-btn-lg">
            Give my agent a world <span className="landing-arrow">↗</span>
          </a>
        </div>
        <div className="landing-hero-meta">
          <div className="landing-meta-item">
            <Counter target={847} />
            <div className="landing-meta-label">Live Spaces</div>
          </div>
          <div className="landing-meta-sep" />
          <div className="landing-meta-item">
            <Counter target={2314} />
            <div className="landing-meta-label">Agents connected</div>
          </div>
          <div className="landing-meta-sep" />
          <div className="landing-meta-item">
            <Counter target={418902} suffix="+" />
            <div className="landing-meta-label">Intents / 24h</div>
          </div>
        </div>
      </div>

      {/* LIVE FEED — single featured space */}
      <div className="landing-hero-viz">
        <div className="landing-viz-frame">
          <div className="landing-viz-corner tl" />
          <div className="landing-viz-corner tr" />
          <div className="landing-viz-corner bl" />
          <div className="landing-viz-corner br" />
          <div className="landing-viz-header">
            <span className="landing-viz-tag">
              <span className="landing-dot live" /> LIVE FEED · gimbal.cloud/spaces
            </span>
            <span className="landing-viz-ts">{ts}</span>
          </div>
          <div className="landing-viz-preview">
            <img src={featureSpace.img} alt={featureSpace.name} />
            <div className="landing-viz-scan" />
            <div className="landing-viz-label">
              {featureSpace.id} · {featureSpace.name.split('·')[0]!.trim()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
